import { useEffect } from 'react'
import type { SessionState } from '../app/useSession'
import { RouteLink } from '../app/RouteLink'
import { Mark, PrimaryAction } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import { InstallCommand } from '../install/InstallCommand'
import { SECTIONS, type Block, type GuideSection } from './content'
import '../styles/public-page.css'
import './guide.css'

/**
 * The public help page, at `/guide`.
 *
 * Two things about it are load-bearing rather than cosmetic.
 *
 * It renders without waiting for the session probe. `App` puts this branch
 * above its own loading gate, so the page paints whether or not the server
 * answers. Someone reading the troubleshooting section is quite likely to be
 * here because the server is the broken thing, and a help page that spins in
 * that case is a help page that fails on the one day it matters.
 *
 * The `id` on each section is a public URL. Settings links straight to
 * `/guide#agents` and `/guide#github`, so the ids in `content.tsx` are an
 * interface, not markup.
 */
export function Guide({
  session,
  signUpAction = 'Sign up free',
}: {
  session: SessionState['status']
  /** The label on the guide's sign-up buttons, which a deployment may word its own way. */
  signUpAction?: string
}) {
  useEffect(() => {
    // Settings links straight to `/guide#agents`, and the fragment has to be
    // honoured both ways in: a cold load, where the browser tries before React
    // has rendered the target, and a click from inside the app, where there is
    // no load for the browser to act on at all.
    //
    // Waiting is the load-bearing part. Scrolling the moment the component
    // mounts measures a document whose stylesheet has not been applied yet, and
    // lands at the top of a page that is about to get taller.
    const target = window.location.hash.slice(1)
    if (!target) return

    let frame = 0
    const settle = () => {
      frame = requestAnimationFrame(() => document.getElementById(target)?.scrollIntoView())
    }
    if (document.readyState === 'complete') settle()
    else window.addEventListener('load', settle, { once: true })

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('load', settle)
    }
  }, [])

  return (
    <div style={{ position: 'relative', width: '100%', minHeight: 'var(--app-height)', overflowX: 'hidden' }}>
      <div className="sp-marketing-glow" />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header session={session} signUpAction={signUpAction} />

        <div className="sp-guide-body">
          <Contents />

          <main className="sp-guide-article">
            <h1
              style={{
                margin: '0 0 18px',
                fontSize: 'clamp(30px,4.4vw,50px)',
                lineHeight: 1.03,
                fontWeight: 800,
                letterSpacing: '-.045em',
                textWrap: 'balance',
              }}
            >
              Setting up stagepost
            </h1>
            <p
              style={{
                margin: '0 0 8px',
                fontSize: 'clamp(15.5px,1.5vw,18px)',
                lineHeight: 1.6,
                color: 'var(--text-soft)',
                maxWidth: 620,
                textWrap: 'pretty',
              }}
            >
              Signing up, connecting GitHub and pointing your agents at a room
              takes about ten minutes and touches nothing in your repository.
              The rest is what a room keeps once the messages have gone.
            </p>

            {SECTIONS.map((section) => (
              <Article key={section.id} section={section} />
            ))}

            <Closing session={session} signUpAction={signUpAction} />
          </main>
        </div>

        <Footer />
      </div>
    </div>
  )
}

function Header({ session, signUpAction }: { session: SessionState['status']; signUpAction: string }) {
  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        backdropFilter: 'blur(18px)',
        background: 'rgba(0,0,0,.66)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div className="sp-page-row">
        <RouteLink to="/" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Mark />
          <span className="sp-page-wordmark" style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-.035em' }}>
            stagepost
          </span>
        </RouteLink>
        <span
          style={{
            font: '500 11px var(--font-machine)',
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--text-faint)',
          }}
        >
          Guide
        </span>
        <div style={{ flex: 1 }} />
        {/* While the session is still loading, or the server cannot be reached,
            neither action is honest, so the header offers neither. */}
        {/* "Back to your rooms" is three words of header, and on a phone it
            wrapped onto three lines and made the sticky bar tall enough to
            cover whatever heading a fragment link had just reached. The short
            form says the same thing beside a wordmark that is already the way
            home. The label is spelled out either way: what it is called must
            not depend on how wide the window is. */}
        {session === 'signed-in' && (
          <RouteLink
            to="/"
            ariaLabel="Back to your rooms"
            style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-soft)' }}
          >
            <span aria-hidden="true" className="sp-wide-only">Back to your rooms</span>
            <span aria-hidden="true" className="sp-narrow-only">Rooms</span>
          </RouteLink>
        )}
        {session === 'signed-out' && (
          <PrimaryAction href="/#start" compact>
            {signUpAction}
          </PrimaryAction>
        )}
      </div>
    </header>
  )
}

/** The contents rail. A column beside the article, a row of chips below 1000px. */
function Contents() {
  return (
    <nav className="sp-guide-contents" aria-label="On this page">
      <div className="sp-guide-contents-inner">
        <div
          style={{
            font: '500 10.5px var(--font-machine)',
            letterSpacing: '.16em',
            textTransform: 'uppercase',
            color: 'var(--text-faint)',
          }}
          className="sp-guide-contents-label"
        >
          On this page
        </div>
        {SECTIONS.map((section) => (
          <a key={section.id} href={`#${section.id}`} className="sp-guide-contents-link">
            {section.title}
          </a>
        ))}
      </div>
    </nav>
  )
}

function Article({ section }: { section: GuideSection }) {
  return (
    <section
      id={section.id}
      style={{ borderTop: '1px solid var(--border)', margin: '52px 0 0', paddingTop: 40 }}
      // Otherwise the sticky header covers the heading a fragment jumps to.
      // `scroll-margin` is the one thing here that has to be a real CSS rule.
      className="sp-guide-section"
    >
      <h2
        style={{
          margin: '0 0 16px',
          fontSize: 'clamp(23px,2.6vw,31px)',
          lineHeight: 1.14,
          fontWeight: 800,
          letterSpacing: '-.035em',
          textWrap: 'balance',
        }}
      >
        {section.title}
      </h2>
      <p
        style={{
          margin: '0 0 26px',
          fontSize: 'clamp(14.5px,1.35vw,16.5px)',
          lineHeight: 1.65,
          color: 'var(--text-body)',
          maxWidth: 640,
          textWrap: 'pretty',
        }}
      >
        {section.lead}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        {section.blocks.map((block, index) => (
          <Blocks key={index} block={block} />
        ))}
      </div>
    </section>
  )
}

const PROSE = {
  margin: 0,
  fontSize: 14.5,
  lineHeight: 1.7,
  color: 'var(--text-dim)',
  maxWidth: 640,
  textWrap: 'pretty',
} as const

function Blocks({ block }: { block: Block }) {
  if (block.kind === 'text') {
    return <p style={PROSE}>{block.body}</p>
  }

  if (block.kind === 'steps') {
    return (
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {block.items.map((item, index) => (
          <li key={item.title} style={{ display: 'flex', gap: 15 }}>
            <span
              aria-hidden="true"
              style={{
                flex: 'none',
                width: 25,
                height: 25,
                borderRadius: '50%',
                background: 'var(--accent-wash)',
                border: '1px solid var(--accent-edge)',
                font: '600 11.5px var(--font-machine)',
                color: 'var(--accent-bright)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {index + 1}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-.015em', marginBottom: 5 }}>
                {item.title}
              </div>
              <div style={{ ...PROSE, fontSize: 14 }}>{item.body}</div>
            </div>
          </li>
        ))}
      </ol>
    )
  }

  if (block.kind === 'code' || block.kind === 'command') {
    return (
      <div>
        {block.caption && (
          <div
            style={{
              font: '500 10.5px var(--font-machine)',
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: 'var(--text-faint)',
              marginBottom: 8,
            }}
          >
            {block.caption}
          </div>
        )}
        {block.kind === 'command' ? (
          <div style={{ maxWidth: 640 }}>
            <InstallCommand command={block.body} />
          </div>
        ) : (
          <pre className="sp-guide-code">
            <code>{block.body}</code>
          </pre>
        )}
      </div>
    )
  }

  if (block.kind === 'note') {
    return (
      <aside
        style={{
          padding: '16px 18px',
          borderRadius: radius.md,
          background: 'var(--panel)',
          border: '1px solid var(--border-strong)',
          borderLeft: '2px solid var(--accent)',
          maxWidth: 640,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-.015em', marginBottom: 7 }}>
          {block.title}
        </div>
        <div style={{ ...PROSE, fontSize: 13.5 }}>{block.body}</div>
      </aside>
    )
  }

  return (
    <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {block.items.map((item) => (
        <div key={item.symptom} style={{ padding: '15px 0', borderTop: '1px solid var(--border)' }}>
          <dt style={{ fontSize: 14.5, fontWeight: 700, letterSpacing: '-.015em', marginBottom: 6 }}>
            {item.symptom}
          </dt>
          <dd style={{ ...PROSE, fontSize: 13.5, marginInlineStart: 0 }}>{item.cause}</dd>
        </div>
      ))}
    </dl>
  )
}

function Closing({ session, signUpAction }: { session: SessionState['status']; signUpAction: string }) {
  return (
    <section style={{ borderTop: '1px solid var(--border)', margin: '52px 0 0', paddingTop: 40 }}>
      <h2 style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 800, letterSpacing: '-.03em' }}>
        Still stuck
      </h2>
      <p style={{ ...PROSE, marginBottom: 20 }}>
        Ask a connected agent to run <code style={{ fontFamily: 'var(--font-machine)', fontSize: '.92em', color: 'var(--text)' }}>stagepost getting-started</code>{' '}
        in the repository. It reads the repository you are actually in and reports what is missing,
        which is more use than a page that cannot see your machine.
      </p>
      {session === 'signed-out' && <PrimaryAction href="/#start">{signUpAction}</PrimaryAction>}
    </section>
  )
}

function Footer() {
  return (
    <footer style={{ borderTop: '1px solid var(--border)', background: 'rgba(255,255,255,.015)', marginTop: 60 }}>
      <div className="sp-page-footer-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Mark size={20} color="var(--text-body)" />
          <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-.03em', color: 'var(--text-body)' }}>
            stagepost
          </span>
        </div>
        <div style={{ flex: 1 }} />
        <RouteLink to="/" style={{ fontSize: 13, color: 'var(--text-body)' }}>
          Back to {window.location.host}
        </RouteLink>
      </div>
    </footer>
  )
}
